import { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import config from '@plone/registry';
import styles from './styles.module.css';
import type { BlogExpanders } from '@redturtle/qubicaamf-theme/components/View/NewsTestimonialsView/NewsTestimonialView';
import { Tags } from '@redturtle/qubicaamf-theme/components/commons/Tags/Tags';

interface SlidesProps {
  slides: BlogExpanders['blog_summary']['news_macro_types'];
}

const Slides = (props: SlidesProps) => {
  const { slides } = props;
  const Image = config.getComponent('Image').component;
  const [isScrolling, setIsScrolling] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if the container is scrollable
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const hasOverflow = container.scrollWidth > container.clientWidth;
      setIsScrollable(hasOverflow);
    }
  }, [slides]);

  const handleScroll = () => {
    // Show the scrollbar thumb
    setIsScrolling(true);

    // Clear any previous timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Hide the scrollbar thumb after 1 second of no scrolling
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 100); // Adjust the delay as needed
  };

  return (
    <div
      ref={containerRef}
      className={cx('block-imagegallery-slides', styles.slides, {
        [styles.scrollbar]: isScrolling,
        [styles.justifyCenter]: !isScrollable,
      })}
      onScroll={handleScroll}
    >
      {slides.map((slide) => {
        let slideTags = [];
        if (slide.blog_tags.length > 3) {
          slideTags = slide.blog_tags.slice(0, 3);
          slideTags.push('...');
        } else {
          slideTags = slide.blog_tags;
        }
        return (
          <div key={slide['@id']} className={styles.slideContainer}>
            <div className={styles.slide}>
              <UniversalLink
                item={slide['@id']}
                href={slide['@id']}
                title={slide.title}
                openLinkInNewTab={true}
                className={styles.slideLink}
              >
                {slide.image_scales ? (
                  <Image
                    alt={slide.title ?? ''}
                    item={slide}
                    className={styles.slideImg}
                  />
                ) : (
                  <div className={styles.noImgSlide} />
                )}
                <div className={styles.slideTitle}>
                  <div className={styles.slideTitleButton}>{slide.title}</div>
                </div>
              </UniversalLink>
            </div>
            {slide.blog_tags?.length > 0 && (
              <Tags
                ariaLabel="Blog categories"
                items={(slide.blog_tags.length > 2
                  ? slideTags
                  : slide.blog_tags
                ).map((tag) => {
                  return {
                    tagName: tag,
                    id: tag === '...' ? 'more-tags' : tag,
                    openLinkInNewTab: false,
                    href: tag === '...' ? slide['@id'] : '',
                    cameFrom: '/news',
                  };
                })}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Slides;
